import { useState, useEffect, useRef, useCallback } from 'react';
import { brokers } from '@/data/brokers';

export default function BrokerPopupAd() {
  const [expanded, setExpanded] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isHovering = useRef(false);

  const broker = brokers[currentIndex];

  useEffect(() => {
    if (!expanded) {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
      return;
    }
    autoPlayRef.current = setInterval(() => {
      if (!isHovering.current) {
        setCurrentIndex((prev) => (prev + 1) % brokers.length);
      }
    }, 5000);
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [expanded]);

  const goTo = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  return (
    <>
      {/* ── Collapsed trigger ── */}
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 pl-4 pr-3 py-2.5 rounded-full cursor-pointer bg-foreground-950 text-background-50 border border-white/[0.08] hover:border-white/[0.15] hover:bg-foreground-800 transition-all duration-200"
          aria-label="Compare brokers"
        >
          <span className="text-[12px] font-semibold whitespace-nowrap tracking-tight">Compare</span>
          <div className="w-5 h-5 rounded-full bg-accent-500/20 flex items-center justify-center">
            <i className="ri-arrow-up-s-line text-accent-400 text-[13px]"></i>
          </div>
        </button>
      )}

      {/* ── Expanded card ── */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[300px] max-w-[calc(100vw-48px)] transition-all duration-500 ease-out ${
          expanded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0 pointer-events-none'
        }`}
      >
        <div
          className="relative rounded-[20px] overflow-hidden border border-white/[0.06]"
          style={{ background: 'linear-gradient(145deg, #0d1321 0%, #0a0e18 100%)' }}
          onMouseEnter={() => { isHovering.current = true; }}
          onMouseLeave={() => { isHovering.current = false; }}
        >
          {/* Subtle inner glow top edge */}
          <div className="absolute inset-x-0 top-0 h-[1px] bg-white/[0.08]"></div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-2">
            <span className="text-[10px] font-medium text-white/25 uppercase tracking-[0.14em]">
              Featured
            </span>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-white/20 tabular-nums">
                {currentIndex + 1} / {brokers.length}
              </span>
              <button
                onClick={() => setExpanded(false)}
                className="w-6 h-6 rounded-lg flex items-center justify-center cursor-pointer text-white/25 hover:text-white/50 hover:bg-white/[0.06] transition-colors"
                aria-label="Close"
              >
                <i className="ri-subtract-line text-[12px]"></i>
              </button>
            </div>
          </div>

          <div className="px-5 pb-5">
            <div key={currentIndex} className="animate-fadeIn">
              {/* Broker name */}
              <h3 className="text-[20px] font-bold text-white/90 tracking-[-0.01em] leading-tight">
                {broker.name}
              </h3>

              {/* Highlight pill */}
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500/10 border border-accent-500/15">
                <div className="w-1 h-1 rounded-full bg-accent-400"></div>
                <span className="text-[12px] font-semibold text-accent-400">
                  {broker.highlight}
                </span>
              </div>

              {/* Detail line */}
              <p className="mt-2 text-[11px] text-white/30 leading-relaxed">
                {broker.detail}
              </p>

              {/* CTA */}
              <a
                href={broker.href}
                target="_blank"
                rel="sponsored nofollow noopener"
                className="flex items-center justify-center w-full h-11 mt-4 rounded-full text-[13px] font-semibold text-background-50 no-underline whitespace-nowrap transition-all duration-200 cursor-pointer bg-accent-600 hover:bg-accent-500"
                style={{ boxShadow: '0 4px 20px oklch(0.52 0.18 175 / 0.25)' }}
              >
                Open Account
                <i className="ri-arrow-right-up-line text-[14px] ml-1.5"></i>
              </a>
            </div>

            {/* Dot navigation */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {brokers.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goTo(idx)}
                  className={`rounded-full cursor-pointer transition-all duration-300 ${
                    idx === currentIndex
                      ? 'w-5 h-[5px] bg-white/60'
                      : 'w-[5px] h-[5px] bg-white/15 hover:bg-white/30'
                  }`}
                  aria-label={`Broker ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}